/* PXW · Gift list ladder — keeps the product-page ladder ([data-pxw-giftlist]) and the cart-drawer
   bar ([data-pxw-glbar]) in step with the cart. Both are rendered server-side; this only repaints
   them when the cart changes: it listens to the theme's cart:update event (CartAddEvent /
   CartUpdateEvent from assets/events.js) and re-reads /cart.js. Tier quantities, labels and the
   free-shipping threshold are read from the elements' data attributes (theme settings). */
(() => {
  if (window.__pxwGiftlist) return;
  window.__pxwGiftlist = true;

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };
  const int = (v, d) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : d;
  };
  const WORDS = { 1: 'one more', 2: 'two more', 3: 'three more', 4: 'four more' };

  function tiers(el) {
    return [1, 2, 3].map((i) => ({ i, q: int(el.dataset['q' + i], 0) })).filter((t) => t.q > 0);
  }

  function reachedTier(el, count) {
    let r = 0;
    tiers(el).forEach((t) => {
      if (count >= t.q) r = t.i;
    });
    return r;
  }

  function paintLadder(el, cart) {
    const count = cart.item_count;
    const r = reachedTier(el, count);
    el.dataset.count = String(count);
    el.dataset.reached = String(r);
    el.querySelectorAll('[data-tier]').forEach((cell) => {
      const on = int(cell.dataset.tier, 0) === r;
      cell.classList.toggle('is-reached', on);
      if (on) cell.setAttribute('aria-current', 'step');
      else cell.removeAttribute('aria-current');
    });
  }

  function paintBar(el, cart) {
    const count = cart.item_count;
    const subtotal = cart.total_price;
    const currency = cart.currency || el.dataset.currency;
    if (!count) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    // Horizon re-renders this bar server-side on every cart change; only repaint when the
    // markup is behind the cart, so the Liquid money format is not overwritten needlessly.
    if (el.dataset.count === String(count) && el.dataset.subtotal === String(subtotal)) return;
    el.dataset.count = String(count);
    el.dataset.subtotal = String(subtotal);

    const r = reachedTier(el, count);
    const ts = tiers(el);
    const next = ts.find((t) => t.q > count) || null;
    const threshold = int(el.dataset.threshold, 0);

    const countEl = el.querySelector('[data-pxw-glbar-count]');
    if (countEl) countEl.textContent = `${count} ${count === 1 ? 'item' : 'items'} · ${money(subtotal, currency)}`;

    const appliedEl = el.querySelector('[data-pxw-glbar-applied]');
    if (appliedEl) {
      // data-applied-1 is dataset['applied-1'] (a hyphen before a digit is not camel-cased).
      const label = r ? el.getAttribute('data-applied-' + r) || '' : '';
      appliedEl.textContent = label;
      appliedEl.hidden = !label;
    }

    let free = true;
    let pct = 100;
    let remaining = 0;
    if (threshold > 0) {
      remaining = threshold - subtotal;
      if (remaining > 0) {
        free = false;
        pct = Math.min(100, Math.floor((subtotal * 100) / threshold));
      } else {
        remaining = 0;
      }
    }
    const fill = el.querySelector('[data-pxw-glbar-fill]');
    if (fill) fill.style.width = pct + '%';

    let line;
    if (free) {
      line = 'Free U.S. shipping applied';
    } else {
      line = `${money(remaining, currency)} to free U.S. shipping`;
      const clause = next ? el.getAttribute('data-next-' + next.i) || '' : '';
      if (next && clause) {
        const need = next.q - count;
        line += ` · add ${WORDS[need] || need + ' more'} and ${clause}`;
      }
    }
    const lineEl = el.querySelector('[data-pxw-glbar-line]');
    if (lineEl) lineEl.textContent = line;
  }

  function paint(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    document.querySelectorAll('[data-pxw-giftlist]').forEach((el) => paintLadder(el, cart));
    document.querySelectorAll('[data-pxw-glbar]').forEach((el) => paintBar(el, cart));
  }

  let timer = null;
  function refresh() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then(paint)
        .catch(() => {});
    }, 150);
  }

  // Horizon dispatches cart:update for adds (CartAddEvent) and quantity changes (CartUpdateEvent).
  // Both normally carry the full /cart.js cart as detail.resource (product-form.js re-fetches it);
  // when the resource has no items (error branch, third-party dispatch) re-read /cart.js.
  document.addEventListener('cart:update', (event) => {
    const d = event.detail || {};
    const cart = d.resource && typeof d.resource.item_count === 'number' ? d.resource : null;
    if (cart && Array.isArray(cart.items)) paint(cart);
    else refresh();
  });

  // Back/forward cache can restore a page with a stale ladder.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
})();
